import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../../stores/appStore';
import { Badge } from '../ui';
import { Bell, Moon, Sun, User } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { api } from '../../lib/api';
import type { TenantNotification } from '../../types';
import { toast } from '../../stores/toastStore';

export function Header() {
  const { tenant, fetchTenant } = useAppStore();
  const { theme, setLight, setDark } = useTheme();
  const [isBellOpen, setIsBellOpen] = useState(false);
  const [notifications, setNotifications] = useState<TenantNotification[]>([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [processingNotificationId, setProcessingNotificationId] = useState<string | null>(null);

  const getPlanBadge = (plan: string) => {
    const variants: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
      free: 'default',
      starter: 'info',
      professional: 'success',
      enterprise: 'warning',
    };
    return variants[plan] || 'default';
  };

  const getPlanLabel = (plan: string) => {
    const labels: Record<string, string> = {
      free: 'GRÁTIS',
      starter: 'INICIAL',
      professional: 'PRO',
      enterprise: 'EMPRESA',
    };
    return labels[plan] || plan.toUpperCase();
  };

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.read || notification.status === 'pending').length,
    [notifications],
  );

  const loadNotifications = async () => {
    if (!tenant?.id) {
      setNotifications([]);
      return;
    }

    setIsLoadingNotifications(true);
    try {
      const inbox = await api.getTenantNotifications(tenant.id);
      setNotifications(inbox || []);
    } catch (error) {
      console.error('Failed to load notifications', error);
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  useEffect(() => {
    void loadNotifications();
  }, [tenant?.id]);

  const handleRespondNotification = async (
    notificationId: string,
    action: 'accept' | 'reject',
  ) => {
    if (!tenant?.id) return;

    setProcessingNotificationId(notificationId);
    try {
      if (action === 'accept') {
        await api.acceptTenantNotification(tenant.id, notificationId);
        toast.success('Solicitação aceita com sucesso.');
      } else {
        await api.rejectTenantNotification(tenant.id, notificationId);
        toast.success('Solicitação recusada.');
      }

      await loadNotifications();
      await fetchTenant(tenant.id);
    } catch (error: any) {
      toast.error(error?.message || 'Falha ao processar notificação.');
    } finally {
      setProcessingNotificationId(null);
    }
  };

  return (
    <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6">
      <div>
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {tenant?.name || 'Chatbot Builder'}
        </h1>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700 p-1">
          <button
            type="button"
            onClick={setLight}
            className={`p-1.5 rounded-md transition-colors ${
              theme === 'light'
                ? 'bg-slate-100 text-slate-900'
                : 'text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
            title="Tema claro"
            aria-label="Ativar tema claro"
          >
            <Sun className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={setDark}
            className={`p-1.5 rounded-md transition-colors ${
              theme === 'dark'
                ? 'bg-slate-800 text-slate-100'
                : 'text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
            title="Tema escuro"
            aria-label="Ativar tema escuro"
          >
            <Moon className="w-4 h-4" />
          </button>
        </div>

        {tenant && (
          <Badge variant={getPlanBadge(tenant.subscriptionPlan)}>
            {getPlanLabel(tenant.subscriptionPlan)}
          </Badge>
        )}

        <div className="relative">
          <button
            onClick={() => setIsBellOpen((value) => !value)}
            className="relative p-2 text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {isBellOpen && (
            <div className="absolute right-0 mt-2 w-96 max-w-[90vw] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl z-50">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Notificações</p>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {isLoadingNotifications ? (
                  <p className="px-4 py-3 text-sm text-slate-500 dark:text-slate-300">Carregando...</p>
                ) : notifications.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-slate-500 dark:text-slate-300">Nenhuma notificação.</p>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 last:border-0"
                    >
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{notification.title}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{notification.message}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                        {new Date(notification.createdAt).toLocaleString('pt-BR')}
                      </p>

                      {notification.type === 'partner_link_request' && notification.status === 'pending' && (
                        <div className="flex items-center gap-2 mt-3">
                          <button
                            onClick={() => void handleRespondNotification(notification.id, 'accept')}
                            disabled={processingNotificationId === notification.id}
                            className="px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                          >
                            Aceitar
                          </button>
                          <button
                            onClick={() => void handleRespondNotification(notification.id, 'reject')}
                            disabled={processingNotificationId === notification.id}
                            className="px-3 py-1.5 rounded-md text-xs font-medium bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-60"
                          >
                            Recusar
                          </button>
                        </div>
                      )}

                      {notification.type === 'partner_link_request' && notification.status !== 'pending' && (
                        <p className="text-xs mt-2 text-slate-500 dark:text-slate-400">
                          Status: {notification.status === 'accepted' ? 'Aceito' : 'Recusado'}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <button className="flex items-center gap-2 p-2 text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
          <User className="w-5 h-5" />
        </button>
      </div>
    </header>
  );
}
